import React from 'react';
import { Check } from 'lucide-react';
import { formatEUR } from '../../hooks/usePrices';

/**
 * Configurator — small presentational atoms used in steps 1-3.
 *
 * Each atom is stateless: it receives selection state + handlers as props
 * and renders a single interactive element. Keeps the orchestrator readable.
 */

export function StepIndicator({ step, labels }) {
  return (
    <ol className="flex items-center gap-2 sm:gap-3 mb-8" aria-label="Steps">
      {labels.map((lab, i) => {
        const idx = i + 1;
        const done = step > idx;
        const active = step === idx;
        return (
          <li key={lab} className="flex items-center gap-2 sm:gap-3">
            <span
              data-testid={`cfg-step-pill-${idx}`}
              className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[12px] font-bold tabular-nums transition-colors ${
                done ? 'bg-[#7FC242] text-white'
                : active ? 'bg-[#F26B1A] text-white'
                : 'bg-white border border-[#E5E0D2] text-[#5C5852]'
              }`}
            >
              {done ? <Check className="w-3.5 h-3.5" /> : idx}
            </span>
            <span className={`hidden sm:inline text-[12.5px] font-semibold tracking-wide ${
              active ? 'text-[#1F1D1A]' : 'text-[#5C5852]'
            }`}>{lab}</span>
            {idx < labels.length && <span className="w-4 sm:w-8 h-px bg-[#E5E0D2]" />}
          </li>
        );
      })}
    </ol>
  );
}

export function TypeCard({ entry, selected, onClick, onDoubleClick, productName, priceDisplay }) {
  return (
    <button
      type="button"
      data-testid={`cfg-type-${entry.slug}`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`group relative text-left rounded-2xl border-2 p-4 transition-all ${
        selected
          ? 'border-[#F26B1A] bg-white shadow-[0_18px_40px_-22px_rgba(242,107,26,0.45)]'
          : 'border-[#E5E0D2] bg-white hover:border-[#F26B1A]/40 hover:-translate-y-0.5'
      }`}
    >
      <span aria-hidden="true" className={`absolute top-3 right-3 inline-flex items-center justify-center w-5 h-5 rounded-full border-2 transition-colors ${
        selected ? 'bg-[#F26B1A] border-[#F26B1A]' : 'border-[#E5E0D2]'
      }`}>
        {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      </span>
      <span aria-hidden="true" className="block w-10 h-10 rounded-lg bg-gradient-to-br from-[#F26B1A]/15 to-[#7FC242]/15 mb-3" />
      <p className="text-[14.5px] font-semibold text-[#1F1D1A] leading-tight pr-6">{productName}</p>
      {priceDisplay && (
        <p className="text-[12px] text-[#5C5852] mt-1.5 tabular-nums">{priceDisplay.display}</p>
      )}
    </button>
  );
}

export function ColorChip({ color, selected, onClick, onDoubleClick, label }) {
  return (
    <button
      type="button"
      data-testid={`cfg-color-${color.id}`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      aria-label={label}
      className={`group flex items-center gap-2.5 px-3 py-2 rounded-full border-2 bg-white transition-all ${
        selected ? 'border-[#F26B1A] shadow-[0_8px_18px_-10px_rgba(242,107,26,0.6)]' : 'border-[#E5E0D2] hover:border-[#F26B1A]/40'
      }`}
    >
      <span className="w-5 h-5 rounded-full border border-black/10" style={{ backgroundColor: color.hex }} />
      <span className="text-[13px] font-medium text-[#1F1D1A]">{label}</span>
      {color.up > 0 && <span className="text-[11px] text-[#5C5852] tabular-nums">+{formatEUR(color.up)}</span>}
    </button>
  );
}

export function MeshOption({ mesh, selected, onClick, onDoubleClick, label, desc }) {
  return (
    <button
      type="button"
      data-testid={`cfg-mesh-${mesh.id}`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`text-left p-4 rounded-2xl border-2 bg-white transition-all ${
        selected ? 'border-[#F26B1A] shadow-[0_18px_40px_-22px_rgba(242,107,26,0.45)]' : 'border-[#E5E0D2] hover:border-[#F26B1A]/40 hover:-translate-y-0.5'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-[14px] font-semibold text-[#1F1D1A]">{label}</p>
        {mesh.up > 0 && <span className="text-[12px] text-[#F26B1A] font-bold tabular-nums">+{formatEUR(mesh.up)}</span>}
      </div>
      <p className="text-[12.5px] text-[#5C5852] leading-snug">{desc}</p>
    </button>
  );
}

export function DirectionPicker({ value, onChange, labels, testIdPrefix = 'cfg-dir' }) {
  const items = [
    { id: 'L', label: labels.left },
    { id: 'R', label: labels.right },
  ];
  return (
    <div className="flex gap-2">
      {items.map((d) => {
        const selected = value === d.id;
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onChange(d.id)}
            data-testid={`${testIdPrefix}-${d.id}`}
            className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 text-[14px] font-semibold transition-all ${
              selected
                ? 'bg-[#F26B1A] text-white border-[#F26B1A]'
                : 'bg-white text-[#1F1D1A] border-[#E5E0D2] hover:border-[#F26B1A]/40'
            }`}
          >
            {d.id === 'L' ? '← ' : ''}{d.label}{d.id === 'R' ? ' →' : ''}
          </button>
        );
      })}
    </div>
  );
}
